import React from 'react'

import { IMatchEvent, IMatch, IPlayer, ITeam } from 'protos'
import { injectIntl, WrappedComponentProps, FormattedRelativeTime } from 'react-intl'
import { MatchEventToken } from 'lib/enumTranslations'
import uniqWith from 'lodash-es/uniqWith'
import last from 'lodash-es/last'
import get from 'lodash-es/get'

import BaseGameStatusBar from './components/BaseGameStatusBar'

interface Props {
  events?: Array<IMatchEvent> | null
  players?: { [k: string]: IPlayer } | null
  match: IMatch
  onClick?: () => void
}

type State = {
  lastEvent?: IMatchEvent
  animating: boolean
}

type InjectedProps = Props & WrappedComponentProps

class LastEventBar extends React.Component<InjectedProps, State> {
  constructor(props: InjectedProps) {
    super(props)

    this.unseenEventQueue = []
    this.seenEventIds = new Set()
    this.eventsArrived = false

    this.state = {
      animating: false,
    }
  }

  componentDidMount() {
    this.handleIncomingEvents(this.props.events || [])
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.events !== this.props.events) {
      this.handleIncomingEvents(nextProps.events || [])
    }
  }

  getTeamName = (teamId: string): string | undefined => {
    const homeTeam: ITeam = get(this.props.match, 'homeTeam')!
    const awayTeam: ITeam = get(this.props.match, 'awayTeam')!

    return (homeTeam.teamId === teamId ? homeTeam.name : awayTeam.name) || undefined
  }

  getPlayerName = (playerId: string) =>
    this.props.players && get(this.props.players[playerId], 'name')

  getEventTypeName = (eventTypeId: number) =>
    this.props.intl.formatMessage({ id: MatchEventToken(eventTypeId) })

  unseenEventQueue: Array<IMatchEvent>
  seenEventIds: Set<string>
  eventsArrived: boolean

  handleIncomingEvents(nextEvents: Array<IMatchEvent>) {
    if (this.eventsArrived === false) {
      this.setState({ lastEvent: last(nextEvents) }, () => {
        nextEvents.forEach((event: IMatchEvent) => {
          this.seenEventIds.add(String(event.eventId))
        })

        this.eventsArrived = true
      })
    } else {
      this.unseenEventQueue =
        uniqWith(
          this.unseenEventQueue.concat(nextEvents),
          (a: IMatchEvent, b: IMatchEvent) => a.eventId === b.eventId,
        )
          .filter((event: IMatchEvent) => !this.seenEventIds.has(String(event.eventId)))

      if (this.state.lastEvent) {
        const lastEventId = this.state.lastEvent.eventId
        const updatedLastEvent = nextEvents.find(event => event.eventId === lastEventId)

        this.setState(state => ({ lastEvent: updatedLastEvent || state.lastEvent }))
      }

      if (this.unseenEventQueue.length > 0 && !this.state.animating) {
        this.setState({ animating: true }, () => { this.switchNextEvent() })
      }
    }
  }

  switchNextEvent() {
    if (this.unseenEventQueue.length === 0) {
      this.setState({ animating: false })
      return
    }

    const lastEvent = this.unseenEventQueue.shift()!
    this.seenEventIds.add(String(lastEvent.eventId))

    this.setState({ lastEvent })
    setTimeout(this.switchNextEvent.bind(this), 2500)
  }

  render() {
    const { intl, onClick } = this.props
    const { lastEvent, animating } = this.state

    if (!lastEvent) { return null }

    const eventName = this.getEventTypeName(Number(lastEvent.type))

    const relatedTeamOrPlayerName = lastEvent.playerId
      ? this.getPlayerName(String(lastEvent.playerId))
      : this.getTeamName(String(lastEvent.teamId))

    const addSign = (n: number) => (n <= 0 ? String(n) : `+${n}`)
    const points = addSign(Number(lastEvent.displayValue))
    const pointUnit = intl.formatMessage({ id: 'common_unit_point' })

    return (
      <BaseGameStatusBar
        color="primary"
        onClick={onClick}
        animating={animating}
        key={String(lastEvent.eventId)}
        headline={[
          eventName,
          Boolean(lastEvent.displayValue) && ` ${points}${pointUnit}`,
        ]}
        message={[
          relatedTeamOrPlayerName,
          Boolean(lastEvent.elapsedMin) && [
            ' (',
            <FormattedRelativeTime
              key="elapsed_mins"
              value={lastEvent.elapsedMin!}
              unit="minute"
            />,
            ')',
          ],
        ]}
      />
    )
  }
}

export default injectIntl(LastEventBar)
try {
    // @ts-ignore
    LastEventBar.displayName = "LastEventBar";
    // @ts-ignore
    LastEventBar.__docgenInfo = { "description": "", "displayName": "LastEventBar", "props": { "forwardedRef": { "defaultValue": null, "description": "", "name": "forwardedRef", "required": false, "type": { "name": "IntlShape | ((instance: any) => void) | RefObject<any>" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/GameStatusBar/components/LastEventBar/LastEventBar.tsx#LastEventBar"] = { docgenInfo: LastEventBar.__docgenInfo, name: "LastEventBar", path: "src/components/GameStatusBar/components/LastEventBar/LastEventBar.tsx#LastEventBar" };
}
catch (__react_docgen_typescript_loader_error) { }